import { lazy, type ComponentType } from 'react';

/**
 * Recovering from "Failed to fetch dynamically imported module".
 *
 * Every page is its own hashed chunk (List-RsaGxDpl.js), and each deploy
 * replaces the whole build — so a tab opened before the deploy still asks for
 * chunk names that no longer exist, and the first click on a page it has not
 * visited yet 404s. A hard refresh fixed it because it fetches the new
 * index.html, which names the new chunks. This does that refresh for the
 * user, once, while the Suspense spinner is still showing.
 *
 * "Retry this page" could never fix it: React.lazy caches the rejected
 * import, so re-rendering the same route rejects again without refetching.
 */

const RELOAD_AT_KEY = 'ukn_chunk_reload_at';
// Long enough that a chunk that is missing even after a fresh load (a broken
// deploy) shows the error card instead of reloading forever.
const RELOAD_GUARD_MS = 30_000;

let reloadPending = false;

export function isChunkLoadError(error: unknown): boolean {
  const e = error as { name?: string; message?: string } | null | undefined;
  if (e?.name === 'ChunkLoadError') return true;
  const msg = String(e?.message ?? error ?? '');
  return (
    // Chromium / Firefox / Safari wordings for a failed import()
    /Failed to fetch dynamically imported module/i.test(msg)
    || /error loading dynamically imported module/i.test(msg)
    || /Importing a module script failed/i.test(msg)
    // Vite's preload helper, when a chunk's CSS or JS dependency is gone
    || /Unable to preload CSS/i.test(msg)
    || /Loading (CSS )?chunk [\w-]+ failed/i.test(msg)
  );
}

/**
 * Reload the page to pick up the current build. Returns false — and does not
 * reload — when offline (a reload would only swap our error for the
 * browser's), when a reload already happened in the last 30 s, or when
 * sessionStorage is unavailable (no loop guard means no automatic reload).
 */
export function reloadForNewVersion(): boolean {
  if (reloadPending) return true;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
  try {
    const last = Number(sessionStorage.getItem(RELOAD_AT_KEY) || 0);
    if (Date.now() - last < RELOAD_GUARD_MS) return false;
    sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
  } catch {
    return false;
  }
  reloadPending = true;
  window.location.reload();
  return true;
}

/** React.lazy that reloads onto the new build when its chunk has been
 *  deployed away, instead of surfacing the error. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyRoute<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    factory().catch((error: unknown) => {
      if (isChunkLoadError(error) && reloadForNewVersion()) {
        // Never settles: the spinner stays up until the reload replaces the page.
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    }),
  );
}

/**
 * Vite's preload helper fires `vite:preloadError` when a chunk's dependency
 * (its CSS, a shared vendor chunk) is missing — the same stale-deploy cause,
 * one level down. Calling preventDefault stops Vite rethrowing it, so the
 * error card never flashes up before the reload.
 */
export function installChunkErrorRecovery(): void {
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadForNewVersion()) event.preventDefault();
  });
}
